/**
 * GlobalView — Draggable Hook
 *
 * Shared hook for making popup windows draggable.
 * Uses capture-phase event listeners to prevent Cesium
 * from receiving mouse events during drag.
 */

import { useEffect, useRef, useState, useCallback } from 'react';

interface DragResult {
  /** Style to apply to the popup container */
  style: React.CSSProperties;
  /** Attach to header's onMouseDown for drag initiation */
  onMouseDown: (e: React.MouseEvent) => void;
  /** Whether currently dragging */
  isDragging: boolean;
  /** Reset position to default (call when popup opens for new entity) */
  reset: () => void;
  /** Stop propagation helper for popup container's onMouseDown */
  stopPropagation: (e: React.MouseEvent) => void;
}

/**
 * Hook for draggable popups.
 * Default position is bottom-left (left/bottom CSS).
 * Once dragged, switches to top/left for consistent mouse math.
 *
 * Uses capture-phase listeners to intercept events before Cesium.
 */
export function useDraggable(defaultLeft = 32, defaultBottom = 32): DragResult {
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();

    // Read current position of the popup from its bounding rect
    const popup = e.currentTarget.closest('[data-draggable]') as HTMLElement
      ?? e.currentTarget.parentElement as HTMLElement;
    if (!popup) return;

    const rect = popup.getBoundingClientRect();
    setIsDragging(true);
    dragOffset.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  }, []);

  useEffect(() => {
    if (!isDragging) return;

    const onMouseMove = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      setPos({
        left: e.clientX - dragOffset.current.x,
        top: e.clientY - dragOffset.current.y,
      });
    };

    const onMouseUp = (e: MouseEvent) => {
      e.stopPropagation();
      e.stopImmediatePropagation();
      setIsDragging(false);
    };

    // Capture phase: intercept before ANY other handler (Cesium etc.)
    window.addEventListener('mousemove', onMouseMove, { capture: true });
    window.addEventListener('mouseup', onMouseUp, { capture: true });

    return () => {
      window.removeEventListener('mousemove', onMouseMove, { capture: true });
      window.removeEventListener('mouseup', onMouseUp, { capture: true });
    };
  }, [isDragging]);

  const reset = useCallback(() => {
    setPos(null);
  }, []);

  const stopPropagation = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  // Compute style
  const style: React.CSSProperties = pos
    ? { left: `${pos.left}px`, top: `${pos.top}px`, bottom: 'auto' }
    : { left: `${defaultLeft}px`, bottom: `${defaultBottom}px` };

  if (isDragging) {
    style.cursor = 'grabbing';
  }

  return { style, onMouseDown, isDragging, reset, stopPropagation };
}
